/**
 * 계약서 에디터 툴바
 *
 *  구성 (계약서 작성에 꼭 필요한 것만):
 *    Undo/Redo | 줌 4종 | 폰트 | 크기 | B I U S | 정렬 4종 | 변수
 *
 *  변수 메뉴는 onMouseDown preventDefault 로 에디터 selection 보존 — 클릭 후
 *  실제 삽입 시 정확한 커서 위치에 들어감.
 */
import type { Editor } from "@tiptap/react";
import { useEffect, useState } from "react";
import { AUTO_VARIABLES } from "../contractTemplate";

/** 한글 + 영문 폰트 옵션 */
export const FONTS = [
  { name: "기본", value: "" },
  { name: "맑은 고딕", value: "'Malgun Gothic', '맑은 고딕', sans-serif" },
  { name: "나눔고딕", value: "'NanumGothic', '나눔고딕', sans-serif" },
  { name: "나눔바른고딕", value: "'NanumBarunGothic', '나눔바른고딕', sans-serif" },
  { name: "본고딕 (Noto Sans)", value: "'Noto Sans KR', 'Source Han Sans KR', sans-serif" },
  { name: "Pretendard", value: "Pretendard, sans-serif" },
  { name: "Spoqa Han Sans", value: "'Spoqa Han Sans', sans-serif" },
  { name: "굴림", value: "Gulim, '굴림', sans-serif" },
  { name: "돋움", value: "Dotum, '돋움', sans-serif" },
  { name: "나눔명조", value: "'NanumMyeongjo', '나눔명조', serif" },
  { name: "본명조 (Noto Serif)", value: "'Noto Serif KR', 'Source Han Serif KR', serif" },
  { name: "바탕", value: "Batang, '바탕', serif" },
  { name: "궁서", value: "Gungsuh, '궁서', serif" },
  { name: "함초롬바탕", value: "'HCR Batang', '함초롬바탕', serif" },
  { name: "Arial", value: "Arial, sans-serif" },
  { name: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { name: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { name: "Calibri", value: "Calibri, sans-serif" },
  { name: "Courier", value: "'Courier New', Courier, monospace" },
];

export const SIZES = ["10pt", "11pt", "12pt", "14pt", "16pt", "18pt", "20pt", "24pt", "28pt", "32pt"];
export const LINE_HEIGHTS = [
  { name: "1.0", value: "1" },
  { name: "1.15", value: "1.15" },
  { name: "1.5", value: "1.5" },
  { name: "1.8", value: "1.8" },
  { name: "2.0", value: "2" },
];

interface Props {
  editor: Editor;
  zoomPct: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onZoomFit: () => void;
  /** 필드설정 단계: 텍스트가 잠겨 서식이 무의미 → 줌만 남기고 서식·변수 툴 숨김. */
  fieldMode?: boolean;
}

const Sep = () => <span className="w-px h-5 bg-[#E9E9E9] mx-1" />;

const btnCls = (active: boolean, disabled = false) =>
  `px-2 py-1 text-[12px] rounded ${
    disabled
      ? "text-[#BBB] opacity-50 cursor-not-allowed"
      : active
        ? "bg-[#FF9934] text-white"
        : "hover:bg-[#F5F5F5] text-[#333]"
  }`;

export const ContractEditorToolbar: React.FC<Props> = ({
  editor,
  zoomPct,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onZoomFit,
  fieldMode = false,
}) => {
  const [showVarMenu, setShowVarMenu] = useState(false);
  // ── 커서 위치 / selection 변경마다 toolbar 강제 re-render ──
  //   TipTap editor 는 mutable 객체라 selection 이 바뀌어도 React 가 모름.
  //   transaction · selectionUpdate 이벤트에 카운터 bump 로 re-render 트리거.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const tick = () => forceTick((t) => (t + 1) | 0);
    editor.on("selectionUpdate", tick);
    editor.on("transaction", tick);
    return () => {
      editor.off("selectionUpdate", tick);
      editor.off("transaction", tick);
    };
  }, [editor]);

  // 현재 커서 위치의 서식 — 매 render 마다 즉시 평가 (state 가 아니라 editor 직접 읽기)
  const curFontFamily = (editor.getAttributes("textStyle").fontFamily as string | undefined) || "";
  const curFontSize = (editor.getAttributes("textStyle").fontSize as string | undefined) || "";
  const curLineHeight = (editor.getAttributes("paragraph").lineHeight as string | undefined) ?? "";
  const isBold = editor.isActive("bold");
  const isItalic = editor.isActive("italic");
  const isUnderline = editor.isActive("underline");
  const isStrike = editor.isActive("strike");
  const alignLeft = editor.isActive({ textAlign: "left" }) || (!editor.isActive({ textAlign: "center" }) && !editor.isActive({ textAlign: "right" }) && !editor.isActive({ textAlign: "justify" }));
  const alignCenter = editor.isActive({ textAlign: "center" });
  const alignRight = editor.isActive({ textAlign: "right" });
  const alignJustify = editor.isActive({ textAlign: "justify" });
  const canUndo = editor.can().undo();
  const canRedo = editor.can().redo();

  return (
    <div className="border-b border-[#E9E9E9] bg-white sticky top-0 z-10">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5">
        {/* Undo / Redo */}
        <button type="button" disabled={!canUndo} onClick={() => editor.chain().focus().undo().run()} className={btnCls(false, !canUndo)} title="실행 취소 (Ctrl+Z)">↶</button>
        <button type="button" disabled={!canRedo} onClick={() => editor.chain().focus().redo().run()} className={btnCls(false, !canRedo)} title="다시 실행 (Ctrl+Y)">↷</button>
        <Sep />

        {/* 줌 */}
        <button type="button" onClick={onZoomOut} className={btnCls(false)} title="축소">−</button>
        <button type="button" onClick={onZoomReset} className="px-2 h-7 text-[12px] hover:bg-[#F5F5F5] rounded font-mono" title="100%">{zoomPct}%</button>
        <button type="button" onClick={onZoomIn} className={btnCls(false)} title="확대">+</button>
        <button type="button" onClick={onZoomFit} className={btnCls(false)} title="화면 맞춤">⤢</button>

        {/* 필드설정 단계에서는 텍스트 편집이 잠기므로 서식·변수 툴을 숨긴다(줌만 노출). */}
        {!fieldMode && (
        <>
        <Sep />

        {/* 폰트 */}
        <select
          className="text-[12px] border border-[#E9E9E9] rounded px-1.5 py-0.5 max-w-[140px]"
          value={curFontFamily}
          onChange={(e) => {
            if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
        >
          {FONTS.map((f) => (
            <option key={f.value} value={f.value}>{f.name}</option>
          ))}
          {/* 매핑되지 않은 임포트 폰트도 selected 로 보이도록 fallback option */}
          {curFontFamily && !FONTS.some((f) => f.value === curFontFamily) && (
            <option value={curFontFamily}>{curFontFamily.split(",")[0].replace(/['"]/g, "").trim()}</option>
          )}
        </select>

        {/* 크기 */}
        <select
          className="text-[12px] border border-[#E9E9E9] rounded px-1.5 py-0.5 w-[64px]"
          value={curFontSize}
          onChange={(e) => {
            const cmd: any = editor.chain().focus();
            if (e.target.value) cmd.setMark("textStyle", { fontSize: e.target.value }).run();
            else cmd.setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run();
          }}
        >
          <option value="">크기</option>
          {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          {curFontSize && !SIZES.includes(curFontSize) && (
            <option value={curFontSize}>{curFontSize}</option>
          )}
        </select>
        <Sep />

        {/* B I U S */}
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnCls(isBold)} title="굵게 (Ctrl+B)"><b>B</b></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnCls(isItalic)} title="기울임 (Ctrl+I)"><i>I</i></button>
        <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnCls(isUnderline)} title="밑줄 (Ctrl+U)"><u>U</u></button>
        <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={btnCls(isStrike)} title="취소선"><s>S</s></button>
        <Sep />

        {/* 정렬 */}
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("left").run()} className={btnCls(alignLeft)} title="왼쪽">≡</button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("center").run()} className={btnCls(alignCenter)} title="가운데">≡</button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("right").run()} className={btnCls(alignRight)} title="오른쪽">≡</button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("justify").run()} className={btnCls(alignJustify)} title="양쪽">≡</button>

        {/* 줄간격 — TextStyleKit 의 LineHeight (paragraph attribute 로 적용) */}
        <select
          className="text-[12px] border border-[#E9E9E9] rounded px-1.5 py-0.5"
          value={curLineHeight}
          onChange={(e) => {
            const cmd: any = editor.chain().focus();
            if (e.target.value && typeof cmd.setLineHeight === "function") {
              cmd.setLineHeight(e.target.value).run();
            } else if (typeof cmd.unsetLineHeight === "function") {
              cmd.unsetLineHeight().run();
            }
          }}
          title="줄간격"
        >
          <option value="">줄간격</option>
          {LINE_HEIGHTS.map((lh) => <option key={lh.value} value={lh.value}>{lh.name}</option>)}
        </select>
        <Sep />

        {/* 변수 메뉴 */}
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowVarMenu((v) => !v)}
            className={btnCls(showVarMenu)}
            title="변수 삽입"
          >
            🟢 변수
          </button>
          {showVarMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-[#E9E9E9] rounded shadow-lg z-50 max-h-[300px] overflow-y-auto w-[200px]">
              <div className="text-[10px] font-bold text-[#999] px-2 py-1 border-b">자동 변수</div>
              {AUTO_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    // 커서(넣을 위치)가 없으면 삽입하지 않고 "위치 먼저 클릭" 안내.
                    //  onMouseDown preventDefault 로 본문 포커스가 유지되므로, 포커스가 없다는 것은
                    //  사용자가 본문에 커서를 둔 적이 없다는 뜻.
                    if (!editor.isFocused) {
                      window.dispatchEvent(new CustomEvent("contract-need-caret"));
                      setShowVarMenu(false);
                      return;
                    }
                    editor.chain().focus().insertContent({
                      type: "contractVariable",
                      attrs: { varKey: v.key, label: v.label },
                    }).run();
                    setShowVarMenu(false);
                  }}
                  className="block w-full text-left px-2 py-1.5 text-[12px] hover:bg-[#F0FDF4]"
                >
                  + {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};
